import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LiabilityFormContext } from "../CreateQuote";
import { PricingPlan } from "../../../types/event_public_liability/api/Pricing";
import { fetchEPLPrices } from "../../../helpers/event_public_liability/api/pricingApi";
import useOnComponentDidMount from "../../../hooks/useOnComponentDidMount";
import ServerError from "../../errors/ServerError";
import LiabilityFormStepNavigation from "../LiabilityFormStepNavigation";
import PricingDetail from "../components/PricingDetails";
import { useAuth } from "../../../hooks/useAuth";
import { getRegion } from "../../../helpers/shared/getRegion";
import SgLoadings from "./SgLoadings";

type Props = {
  onNext: VoidFunction | null;
  onPrevious: VoidFunction | null;
};

type PlanItem = {
  price: number;
};

type updateLoadingPriceParams = {
  plans?: any[];
  loading: { type: string; value: number };
  setPricingState: React.Dispatch<React.SetStateAction<any[]>>;
  setFormState: React.Dispatch<React.SetStateAction<any>>;
};

export const getNewPrice = (planItem: PlanItem, loading: any) => {
  let newPrice = 0;
  if (loading.type === "percentage") {
    newPrice = (planItem.price * loading.value) / 100;
  }
  return planItem.price + newPrice;
};

export const getTypeLoading = (type: string) => {
  return { type, value: type === "percentage" ? 1 : 0 };
};

export const updateLoadingPrice = ({ plans, loading, setPricingState, setFormState }: updateLoadingPriceParams) => {
  if (!plans) return;
  let updatedPlans = plans;
  if (loading.type === "percentage") {
    updatedPlans = plans.map((planItem: any) => ({
      ...planItem,
      price: getNewPrice(planItem, loading),
    }));
  }
  setPricingState(updatedPlans);
  setFormState((prev: any) => ({ ...prev, plans: updatedPlans }));
};

const initialLoading = { type: "", value: 0 };

const PricingDetails: FC<Props> = ({ onNext, onPrevious }) => {
  const { t } = useTranslation("pages", { keyPrefix: "eventpl.product" });
  const { state, setFormState, submitError, setSubmitError, isSubmitting } = useContext(LiabilityFormContext);
  const [pricingState, setPricingState] = useState<PricingPlan[]>([]);
  const [serverError, setServerError] = useState(false);
  const [loading, setLoading] = useState(initialLoading);
  const { user } = useAuth();
  const region = getRegion(user);

  const handleTypeChange = (type: string) => {
    setLoading(getTypeLoading(type));
  };
  const handleValueChange = (value: number) => {
    setLoading((prevState: any) => ({ ...prevState, value }));
  };

  useOnComponentDidMount(() => {
    const setPrices = async () => {
      try {
        const { plans: PricingResponse } = await fetchEPLPrices(state, region);
        setPricingState(PricingResponse);
        setFormState({
          ...state,
          plans: PricingResponse,
          plansWithoutLoading: PricingResponse,
          loading: initialLoading,
        });
        setServerError(false);
      } catch (error) {
        console.error(error);
        setServerError(true);
      }
    };

    setPrices();
  });

  useEffect(() => {
    setFormState((prevState: any) => ({ ...prevState, loading }));
    updateLoadingPrice({ plans: state.plansWithoutLoading, loading, setPricingState, setFormState });
  }, [loading]);

  const arePricesLoaded = pricingState.length > 0;
  return (
    <>
      <div className="container-flow flex-1">
        <PricingDetail serverError={serverError} arePricesLoaded={arePricesLoaded} pricingState={pricingState} t={t} />
      </div>
      <div className="container-flow flex-1">
        <SgLoadings loading={loading} onTypeChange={handleTypeChange} onValueChange={handleValueChange} />
      </div>
      {submitError && <ServerError supportEmailId={t("customerSupport.emailId")} displayType="modal" onClose={() => setSubmitError(false)} />}
      <LiabilityFormStepNavigation isLoading={isSubmitting} onBack={onPrevious} onSubmit={!arePricesLoaded ? undefined : onNext} />
    </>
  );
};

export default PricingDetails;
